/**
 * VoiceStudioPro Component
 *
 * Professional split-panel voice selection interface with:
 * - Left panel: Voice browser with search and filters
 * - Right panel: Preview studio with waveform visualization
 * - Advanced voice customization settings
 * - Real-time greeting editor
 */

import { useState, useEffect, useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { VoiceLibraryPanel } from './voice-studio/VoiceLibraryPanel';
import { PreviewStudioPanel } from './voice-studio/PreviewStudioPanel';
import { useElevenLabsVoices, type PresetVoice } from '@/lib/elevenlabs-voices';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { createComponentLogger } from '@/utils/logger';

interface VoiceStudioProProps {
  userId: string;
  userProfile: {
    gender?: string;
    firstName?: string;
    lastName?: string;
  };
  currentVoiceId?: string | null;
  currentAudioUrl?: string | null;
  currentGreeting?: string | null;
  onVoiceConfigured?: () => void;
}

const log = createComponentLogger('VoiceStudioPro', 'voice-studio');

const COMPACT_LAYOUT_WIDTH = 480;

export function VoiceStudioPro({
  userId,
  userProfile,
  currentVoiceId,
  currentAudioUrl,
  currentGreeting,
  onVoiceConfigured
}: VoiceStudioProProps) {
  const panelsContainerRef = useRef<HTMLDivElement>(null);

  // Fetch all available voices (user + library)
  const { voices, loading, error, stats } = useElevenLabsVoices();

  // The studio can live in a narrow dialog even on a wide viewport. Let its
  // own available width decide when the library and preview panels stack.
  const [isCompactLayout, setIsCompactLayout] = useState(false);

  useEffect(() => {
    const container = panelsContainerRef.current;
    if (!container) {
      return;
    }

    const updateLayout = (width: number) => {
      setIsCompactLayout(width > 0 && width < COMPACT_LAYOUT_WIDTH);
    };

    updateLayout(container.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') {
      const handleResize = () => updateLayout(container.getBoundingClientRect().width);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        updateLayout(entry.contentRect.width);
      }
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [loading, error]);

  // Currently selected voice for preview
  const [selectedVoice, setSelectedVoice] = useState<PresetVoice | null>(null);

  // Currently playing voice ID
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);

  // Log voice library stats when loaded
  useEffect(() => {
    if (!loading && !error) {
      log.info('Voice library loaded', {
        total: stats.total,
        userVoices: stats.userVoices,
        libraryVoices: stats.libraryVoices
      });
    }
  }, [loading, error, stats]);

  // Track prop changes to debug UI update issues
  useEffect(() => {
    log.debug('Props updated', {
      userId,
      currentVoiceId,
      hasAudioUrl: !!currentAudioUrl,
      hasGreeting: !!currentGreeting
    });
  }, [userId, currentVoiceId, currentAudioUrl, currentGreeting]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[600px] border rounded-lg bg-muted/20">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <div>
            <p className="font-medium">Loading Voice Library...</p>
            <p className="text-sm text-muted-foreground">
              Fetching professional voices from ElevenLabs
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load voices: {error}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">AI Voice Studio</h3>
          <p className="text-sm text-muted-foreground">
            {stats.total.toLocaleString()} professional voices available
            {stats.libraryVoices > 0 && (
              <span className="ml-2">
                ({stats.userVoices} yours + {stats.libraryVoices} library)
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Split Panel Layout */}
      <div
        ref={panelsContainerRef}
        data-testid="voice-studio-panels"
        data-orientation={isCompactLayout ? 'vertical' : 'horizontal'}
        className={`border rounded-lg overflow-hidden bg-background ${
          isCompactLayout
            ? 'h-[min(900px,calc(100dvh-10rem))] min-h-[620px]'
            : 'h-[min(700px,calc(100vh-12rem))]'
        }`}
      >
        <PanelGroup
          direction={isCompactLayout ? 'vertical' : 'horizontal'}
        >
          {/* Left Panel: Voice Library Browser */}
          <Panel
            defaultSize={isCompactLayout ? 48 : 35}
            minSize={isCompactLayout ? 35 : 25}
            maxSize={isCompactLayout ? 65 : 50}
            className="min-h-0 min-w-0 bg-muted/20"
          >
            <VoiceLibraryPanel
              voices={voices}
              selectedVoice={selectedVoice}
              playingVoiceId={playingVoiceId}
              onVoiceSelect={setSelectedVoice}
              onVoicePlay={setPlayingVoiceId}
              currentVoiceId={currentVoiceId}
            />
          </Panel>

          {/* Resize Handle */}
          <PanelResizeHandle
            className={isCompactLayout
              ? 'h-1 bg-border hover:bg-primary/50 transition-colors cursor-row-resize'
              : 'w-1 bg-border hover:bg-primary/50 transition-colors cursor-col-resize'}
          />

          {/* Right Panel: Preview Studio */}
          <Panel
            defaultSize={isCompactLayout ? 52 : 65}
            minSize={isCompactLayout ? 35 : 50}
            className="min-h-0 min-w-0"
          >
            <div className={isCompactLayout
              ? 'h-full min-w-0 [&>div>div:first-child]:p-4 [&>div>div:nth-child(2)]:p-4 [&>div>div:last-child]:p-4 [&>div>div:last-child>div]:flex-col [&>div>div:last-child>div>button]:w-full'
              : 'h-full min-w-0'}
            >
              <PreviewStudioPanel
                userId={userId}
                userProfile={userProfile}
                selectedVoice={selectedVoice}
                playingVoiceId={playingVoiceId}
                currentAudioUrl={currentAudioUrl}
                currentGreeting={currentGreeting}
                onPlayingChange={setPlayingVoiceId}
                onVoiceConfigured={onVoiceConfigured}
              />
            </div>
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
}
